/* eslint-disable no-unused-vars */
import { X } from 'lucide-react';
import React from 'react';
import { createIngestionSource, getCindroServerBaseURL } from '../../api';
import { useSession } from '../../contexts/SessionContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import type { UseActionDialogReturn } from '../../hooks/useActionDialog';

type IngestionSourceDialogProps = {
  actionDialogReturn: UseActionDialogReturn;
  onNavigate: (path: string, opts?: { replace?: boolean }) => void;
};

function IngestionSourceDialog({ actionDialogReturn, onNavigate: _onNavigate }: IngestionSourceDialogProps) {
  const {
    actionDialog,
    dialogBusy,
    dialogError,
    setActionDialog,
    setDialogBusy,
    setDialogError,
  } = actionDialogReturn;

  const { session } = useSession();
  const { setWorkspaceVersion } = useWorkspace();

  if (!actionDialog || actionDialog.kind !== 'ingestion-source') return null;

  const close = () => { if (!dialogBusy) setActionDialog(null); };

  const canSubmit = actionDialog.name.trim().length >= 2;

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!session || !actionDialog || actionDialog.kind !== 'ingestion-source' || dialogBusy) return;
    setDialogBusy(true);
    setDialogError('');
    try {
      const name = actionDialog.name.trim();
      if (name.length < 2) {
        setDialogError('Source name must be at least 2 characters long.');
        return;
      }
      const created = await createIngestionSource(session.accessToken, {
        organizationId: actionDialog.organizationId,
        projectId: actionDialog.projectId || undefined,
        name,
        targetEvidenceType: actionDialog.sourceKind === 'grafana' ? 'alert' : actionDialog.targetEvidenceType,
        sourceKind: actionDialog.sourceKind
      });
      setWorkspaceVersion((v) => v + 1);
      setActionDialog({
        kind: 'ingestion-source-reveal',
        name: created.name,
        sourceKind: created.sourceKind,
        webhookUrl: `${getCindroServerBaseURL()}${created.webhookPath}`,
        secret: created.secret
      });
    } catch (err: any) {
      setDialogError(err?.response?.data?.message || err?.message || 'Something went wrong. Please try again.');
    } finally {
      setDialogBusy(false);
    }
  }

  return (
    <div className="dialogOverlay" role="presentation" onClick={close}>
      <form className="dialogPanel" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <div className="dialogHeader">
          <div>
            <div className="panelKicker">Ingestion</div>
            <h3>Add alert source</h3>
            <p>Create a signed webhook endpoint for alerts from another system.</p>
          </div>
          <button type="button" className="iconButton" onClick={close} aria-label="Close dialog">
            <X size={16} />
          </button>
        </div>
        <div className="settingsForm">
          <label className="settingsField">
            <span>Source type</span>
            <select
              value={actionDialog.sourceKind}
              onChange={(e) => {
                const sourceKind = e.target.value as 'generic' | 'grafana';
                setActionDialog({
                  ...actionDialog,
                  sourceKind,
                  targetEvidenceType: sourceKind === 'grafana' ? 'alert' : actionDialog.targetEvidenceType
                });
              }}
            >
              <option value="generic">Generic webhook</option>
              <option value="grafana">Grafana</option>
            </select>
            <p className="fieldHint">
              {actionDialog.sourceKind === 'grafana'
                ? 'Static bearer-token auth, matching Grafana\'s unified-alerting webhook contact point.'
                : 'HMAC-signed webhook with a custom field mapping.'}
            </p>
          </label>
          <label className="settingsField">
            <span>Name</span>
            <input
              value={actionDialog.name}
              onChange={(e) => setActionDialog({ ...actionDialog, name: e.target.value })}
              placeholder={actionDialog.sourceKind === 'grafana' ? 'Grafana alerts' : 'Support desk webhook'}
              minLength={2}
              autoFocus
            />
            <p className="fieldHint">Appears in the alert sources list.</p>
          </label>
          {actionDialog.sourceKind === 'generic' ? (
            <label className="settingsField">
              <span>Evidence type</span>
              <select
                value={actionDialog.targetEvidenceType}
                onChange={(e) => setActionDialog({
                  ...actionDialog,
                  targetEvidenceType: e.target.value as typeof actionDialog.targetEvidenceType
                })}
              >
                <option value="alert">Alert</option>
                <option value="work_item">Work item</option>
                <option value="feedback">Feedback</option>
                <option value="generic">Generic</option>
              </select>
              <p className="fieldHint">Determines how inbound payloads are normalized.</p>
            </label>
          ) : (
            <p className="fieldHint">Grafana sources always target the <strong>alert</strong> evidence type.</p>
          )}
        </div>
        {dialogError && <p className="fieldError">{dialogError}</p>}
        <div className="dialogActions">
          <button type="button" className="secondaryButton" onClick={close} disabled={dialogBusy}>Cancel</button>
          <button type="submit" className="primaryButton" disabled={dialogBusy || !canSubmit}>
            {dialogBusy ? 'Creating…' : 'Create source'}
          </button>
        </div>
      </form>
    </div>
  );
}

export { IngestionSourceDialog };
